/**
 * A line of room icons (item BI1): each a small picture with its number, read out as its words ("2 beds", "1 sofa
 * bed", "Sleeps up to 4", "1 cot", "1 crib") and showing them when a pointer rests on it. What the icons are is decided
 * in roomIcons.ts; this only draws them, with the icon set the rest of the app uses.
 */
import { Baby, BedDouble, BedSingle, Sofa, Users, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { RoomIcon, RoomIconKind } from './roomIcons';

const ICON: Record<RoomIconKind, LucideIcon> = { beds: BedDouble, sofa: Sofa, people: Users, cot: BedSingle, crib: Baby };

export function RoomIcons({ items, className, testid = 'room-icons', ids = {}, drawn = false }: { items: readonly RoomIcon[]; className?: string; testid?: string; ids?: Partial<Record<RoomIconKind, string>>;
  /** Draw the number without putting it in the line's text: for a place whose words are read whole (the summary's room line stays "Room 1 · King Room"). */
  drawn?: boolean;
}) {
  if (!items.length) return null;
  return (
    <ul className={cn('flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-ink-2', className)} data-testid={testid}>
      {items.map((it) => {
        const Icon = ICON[it.kind];
        return (
          <li key={it.kind} className="inline-flex">
            <span role="img" aria-label={it.text} title={it.text} className="inline-flex items-center gap-1 tabular-nums" data-room-icon={it.kind} data-count={it.count} data-testid={ids[it.kind]}>
              <Icon className="size-4 shrink-0" aria-hidden />
              {it.numbered && (drawn ? <span aria-hidden data-n={it.count} className="after:content-[attr(data-n)]" /> : <span aria-hidden>{it.count}</span>)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
